import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";

import toast from "components/ui/toast/Toast";
import { AiChatQuery, AiChatsQuery } from "lib/generated/app-service-gql/graphql";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useAiContext } from "lib/providers/AiContextProvider";
import { useAiChat } from "./useAiChat";
import { useAiChatStreamSubscription } from "./useAiChatStreamSubscription";
import { useInitAiChat } from "./useInitAiChat";

export function useAiChatManager() {
  const [chatId, setChatId] = useState<string>("");

  const [isWaitingForAnswerToStart, setIsWaitingForAnswerToStart] = useState<boolean>(false);

  const { resourceId, resourceType } = useAiContext();

  const client = useQueryClient();

  const { data: currentUser } = useCurrentUser();

  // Fetch previous messages for the conversation
  const { data: conversation, isLoading: isPreviousMessagesLoading } = useAiChat(chatId);

  // Init ai chat subscription
  const { mutate: startAiChatSubscription, reset: resetAiChatSubscription } = useInitAiChat({
    onSuccess: (data) => {
      setIsWaitingForAnswerToStart(true);

      // Add the new chat to the chat list
      let _chatId = chatId;
      if (!_chatId) {
        _chatId = data.initAiChat.conversationId;
        client.setQueryData<InfiniteData<AiChatsQuery>>(
          ["aiChats", resourceType, resourceId],
          (oldData) => {
            const newChat = {
              conversationId: _chatId,
              title: data.initAiChat.question,
            };

            // Create cache if it doesn't exist
            if (!oldData) {
              return { pageParams: [1], pages: [{ aiChats: [newChat] }] };
            }

            // Add the new chat to the first page
            return {
              ...oldData,
              pages: oldData.pages.map((page, index) => {
                if (index === 0) {
                  return {
                    ...page,
                    aiChats: [newChat, ...page.aiChats],
                  };
                }

                return page;
              }),
            };
          },
        );
      }

      // Add the user question to the messages list
      client.setQueryData(["aiChat", _chatId], (oldData: AiChatQuery | undefined) => {
        const newQaContent = { question: data.initAiChat.question, answer: "" };

        if (oldData?.aiChat.messages) {
          return {
            ...oldData,
            aiChat: { ...oldData.aiChat, messages: [...oldData.aiChat.messages, newQaContent] },
          };
        } else {
          return { aiChat: { conversationId: _chatId, messages: [newQaContent] } };
        }
      });

      setChatId(_chatId);
    },
    onError: () => {
      toast.error("Failed to start AI chat. Please try again.");
    },
  });

  // Reset the subscription when the component unmounts
  useEffect(() => {
    return () => {
      resetAiChatSubscription();
    };
  }, [resetAiChatSubscription]);

  // Listen to ai chat stream subscription and update cache
  const { data: subscriptionData } = useAiChatStreamSubscription(chatId, {
    enabled: Boolean(chatId && currentUser),
    onData(data, errors) {
      if (errors && errors.length > 0) {
        Sentry.captureException(errors);
        toast.error("Something went wrong with the AI chat. Please try again.");
        setIsWaitingForAnswerToStart(false);
        return;
      }

      const {
        conversationId,
        question,
        answer: answerOrNull,
        sources,
        isComplete,
      } = data.onAiChatResponse;
      const answer = answerOrNull ?? "";

      // Stop the initial spinner once there is an answer to show, or the stream signals completion.
      // A blank-but-complete terminal event (e.g. the "Sorry..." error fallback) must clear it too,
      // otherwise the chat spins forever when the AI produces an empty answer.
      if (answer || isComplete) {
        setIsWaitingForAnswerToStart(false);
      }

      client.setQueryData(["aiChat", conversationId], (oldData: AiChatQuery | undefined) => {
        // This case is unlikely if optimistic update happened, but as a fallback
        if (!oldData?.aiChat?.messages?.length) {
          return {
            aiChat: {
              conversationId,
              messages: [{ question, answer, sources }],
            },
          };
        }

        // Update the last message with the new answer and sources
        const messages = [...oldData.aiChat.messages];
        messages[messages.length - 1] = {
          ...messages[messages.length - 1],
          answer,
          sources,
        };
        return { ...oldData, aiChat: { ...oldData.aiChat, messages } };
      });
    },
  });

  const handleSetChatId = (id: string) => {
    // Reset waiting state when changing chat from outside
    setIsWaitingForAnswerToStart(false);
    setChatId(id);
  };

  const isLoading =
    isWaitingForAnswerToStart || subscriptionData?.onAiChatResponse?.isComplete === false;

  return {
    chatId,
    setChatId: handleSetChatId,
    conversation,
    thinking: subscriptionData?.onAiChatResponse?.thinking,
    isLoading,
    isWaitingForAnswerToStart,
    isPreviousMessagesLoading,
    startAiChatSubscription,
    resetAiChatSubscription,
  };
}
